import React, { useEffect, useRef, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Toolbar,
  Typography,
  Container,
  Menu,
  MenuItem,
  useMediaQuery,
  Divider,
  Modal,
  Button,
} from "@mui/material";
import Logo from "../../assets/images/japaLogo.png";
import { headerLinks } from "./data";
import { getSurveyList, getUserData, sendInviteForLabsApi, setUserData, getSiteSettingsApi } from "../../redux/action/user";
import "./header.scss";
import SnackBar from "../../atoms/snackBar";
import CloseIcon from "@mui/icons-material/Close";
import CenterLoading from "../core/CenterLoading";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { isSubscriptionActive } from "../../common";
import ZohoScriptEmbed from "./ZohoScriptEmbed";
import MembersOnlyModal from "../MembersOnlyModal";
import OrderLabsDealModal from "../OrderLabsDealModal";
import { hasActiveMembership, getMemberWordPressUrl } from "../../utils/wordpressAccess";
import { JAPA_SEARCH_URL } from "../../constants/externalUrls";
import { hasFeature, MEMBERSHIP_FEATURE_KEYS } from "../../utils/membershipFeatures";
import { normalizeLabPricing } from "../../utils/labPricing";

const Header = (props) => {
  const navigate = useNavigate();
  const token = localStorage.getItem("userKey");
  const [openModal, setOpenModal] = useState(false)
  const [openZohoLabModal, setOpenZohoLabModal] = useState(false);
  const [openMembershipModal, setOpenMembershipModal] = useState(false);
  const [membershipModalMessage, setMembershipModalMessage] = useState("");
  const [zohoScript, setZohoScript] = useState("");
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [loading, setLoading] = useState(false)
  const [openSnackBar, setOpenSnackBar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [labPricing, setLabPricing] = useState(normalizeLabPricing());

  const isSmallScreen = useMediaQuery("(max-width: 900px)");

  const location = useLocation();
  const dispatch = useDispatch();
  const isSubscription = isSubscriptionActive(props?.user);
  const isMember = hasActiveMembership(props?.user);
  const canShowOrderLabs = hasFeature(
    props?.user,
    MEMBERSHIP_FEATURE_KEYS.ORDER_LABS
  );
  // Member + entitlement: email/Zoho. Free Plan + entitlement: JOIN NOW upsell.
  const canUseOrderLabs =
    isMember && hasFeature(props?.user, MEMBERSHIP_FEATURE_KEYS.ORDER_LABS);
  const canAccessBookConsult = hasFeature(
    props?.user,
    MEMBERSHIP_FEATURE_KEYS.BOOK_CONSULT
  );
  const canAccessLastSession = hasFeature(
    props?.user,
    MEMBERSHIP_FEATURE_KEYS.LAST_SESSION
  );
  const canAccessCourses = hasFeature(
    props?.user,
    MEMBERSHIP_FEATURE_KEYS.COURSES
  );
  const canAccessMemberships = hasFeature(
    props?.user,
    MEMBERSHIP_FEATURE_KEYS.MEMBERSHIPS
  );
  const canAccessShop =
    hasFeature(props?.user, MEMBERSHIP_FEATURE_KEYS.SHOP) &&
    Boolean(props.user?.shopUrl);
  const canAccessCommunity =
    hasFeature(props?.user, MEMBERSHIP_FEATURE_KEYS.COMMUNITY) &&
    Boolean(props.user?.communityUrl);
  const canAccessEvents =
    hasFeature(props?.user, MEMBERSHIP_FEATURE_KEYS.EVENTS) &&
    Boolean(props.user?.eventUrl);
  const canAccessSearch = hasFeature(
    props?.user,
    MEMBERSHIP_FEATURE_KEYS.SEARCH
  );

  const isHeaderLinkVisible = (link) => {
    if (link.id === "home") {
      return (
        props.user?.surveyIdList?.length > 0 && canAccessLastSession
      );
    }
    if (link.id === "courses") return canAccessCourses;
    if (link.id === "plans") return canAccessMemberships;
    if (link.id === "shop") return canAccessShop;
    if (link.id === "community") return canAccessCommunity;
    if (link.id === "events") return canAccessEvents;
    if (link.id === "search") return canAccessSearch;
    return true;
  };
  const headerRef = useRef(null);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;

    const setHeaderHeight = () => {
      document.documentElement.style.setProperty(
        "--japa-header-height",
        `${el.offsetHeight}px`
      );
    };

    setHeaderHeight();
    const observer = new ResizeObserver(setHeaderHeight);
    observer.observe(el);
    return () => observer.disconnect();
  }, [isSubscription, token, isSmallScreen]);

  useEffect(() => {
    if (token) {
      props.getSurveyList();

      const userKey = localStorage.getItem("userKey");
      if (userKey) {
        setLoading(true);
        props
          .getUserData(userKey)
          .finally(() => setLoading(false));
      }
    }
  }, []);

  useEffect(() => {
    if (!openModal) return;
    let cancelled = false;
    getSiteSettingsApi()
      .then((data) => {
        if (!cancelled) setLabPricing(normalizeLabPricing(data));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [openModal, canUseOrderLabs]);


  const handleLogout = async () => {
    // Preserve keys we intentionally keep across logout.
    const coupon = localStorage.getItem("couponCode");
    const seenOfferKeys = Object.keys(localStorage).filter((k) =>
      k.startsWith("firstLoginUpgradeOfferSeen:")
    );
    const seenOfferValues = seenOfferKeys.map((k) => [k, localStorage.getItem(k)]);

    // Clear everything
    localStorage.clear();
    sessionStorage.clear();

    window.location.href = 'https://japa.health/?user_logout=true';
    if (coupon != null) localStorage.setItem("couponCode", coupon);
    // Restore first-login offer seen flags so the modal doesn't reappear after logout/login.
    seenOfferValues.forEach(([k, v]) => {
      if (v != null) localStorage.setItem(k, v);
    });
  };


  const handleMenuClose = () => {
    setAnchorElD(null);
  };

  const surveyIdItem =
    props.user?.surveyIdList?.[props.user?.surveyIdList?.length - 1];

  const goToMemberships = () => {
    setOpenModal(false);
    setOpenMembershipModal(false);
    setOpenZohoLabModal(false);
    setAnchorElD(null);
    navigate("/plans");
  };

  const getRedirectPath = async (event, link) => {
    if (link.id !== "home" && link.id !== "shop" && link.id !== "community" && link.id !== "events" && link.id !== "search") {
      return;
    }
    event.preventDefault();
    if (link.id === 'home') {
      if (!surveyIdItem) {
        navigate("/result");
        return;
      }
      localStorage.removeItem('review');
      if (surveyIdItem.s_IsSurveyCompleted === 1) {
        localStorage.setItem("progressDone", true);
      } else {
        localStorage.removeItem("progressDone");
        localStorage.removeItem("detailDone");
      }

      if (
        surveyIdItem.Height &&
        surveyIdItem.Weight &&
        surveyIdItem.ZipCode &&
        surveyIdItem.Age &&
        surveyIdItem.Gender
      ) {
        if (surveyIdItem.Score === 0) {
          localStorage.removeItem("detailDone");
        } else {
          localStorage.setItem("detailDone", true);
        }
      } else {
        localStorage.removeItem("detailDone");
      }

      if (localStorage.getItem("progressDone") === null) {
        navigate(`/progress`);
      } else if (localStorage.getItem("detailDone") === null) {
        navigate("/profiledetail");
      } else {
        navigate(`/result`);
      }
      if (surveyIdItem?.SurveyId != null) {
        localStorage.setItem("assesmentKey", surveyIdItem.SurveyId);
        dispatch(setUserData({ surveyId: surveyIdItem.SurveyId }));
      }
    } else if (link.id === 'shop') {
      if (props.user?.shopUrl) window.location.href = props.user.shopUrl;
    } else if (link.id === 'community') {
      if (props.user?.communityUrl) window.location.href = props.user.communityUrl;
    } else if (link.id === 'events') {
      if (props.user?.eventUrl) window.location.href = props.user.eventUrl;
    } else if (link.id === 'search') {
      if (!hasActiveMembership(props?.user)) {
        window.location.href = JAPA_SEARCH_URL;
        return;
      }
      handleWordPressMemberRedirect({
        urlKey: "searchUrl",
        membersOnlyMessage: "Join to access Search on Japa Health.",
      });
    } else {
      navigate(link.pathname)
    }
  };

  const closeZohoLabModal = () => {
    setOpenZohoLabModal(false);
    setZohoScript("");
  };

  const handleWordPressMemberRedirect = async ({
    urlKey,
    membersOnlyMessage,
    requireMembership = true,
  }) => {
    handleMenuClose();
    handleMenuCloseD();

    if (requireMembership && !hasActiveMembership(props?.user)) {
      setMembershipModalMessage(membersOnlyMessage);
      setOpenMembershipModal(true);
      return;
    }

    setLoading(true);
    try {
      let url = getMemberWordPressUrl(props?.user, urlKey);

      if (!url) {
        const userKey = localStorage.getItem("userKey");
        if (userKey) {
          const refreshed = await props.getUserData(userKey);
          url =
            refreshed?.[urlKey] ||
            refreshed?.rawResponse?.data?.[0]?.[urlKey] ||
            getMemberWordPressUrl(props?.user, urlKey);
        }
      }

      if (!url) {
        setOpenSnackBar({
          open: true,
          message: "Unable to open the page. Please try again.",
          severity: "error",
        });
        return;
      }

      window.location.href = url;
    } catch (error) {
      setOpenSnackBar({
        open: true,
        message: error?.message || "Something went wrong.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBookConsultClick = () => {
    if (!hasFeature(props?.user, MEMBERSHIP_FEATURE_KEYS.BOOK_CONSULT)) {
      handleMenuClose();
      handleMenuCloseD();
      setMembershipModalMessage(
        isMember
          ? "Book a Consult is not included in your current membership. Upgrade your plan to book a consult."
          : "Join to book a consult with Japa Health."
      );
      setOpenMembershipModal(true);
      return;
    }
    handleWordPressMemberRedirect({
      urlKey: "bookConsultUrl",
      membersOnlyMessage: "Join to book a consult with Japa Health.",
      requireMembership: false,
    });
  };

  const handleOrderLabsClick = async () => {
    handleMenuClose();
    handleMenuCloseD();

    // No labs entitlement → same JOIN NOW upsell as non-members (previous UX).
    setOpenModal(true);
  };

  const handleSendInviteForLabs = async () => {
    setLoading(true)
    try {
      const res = await sendInviteForLabsApi();
      setOpenModal(false)
      setOpenConfirmModal(true)

    } catch (error) {
      setOpenSnackBar({
        open: true,
        message: error?.message || "Something went wrong.",
        severity: 'error'
      });
    } finally {
      setLoading(false)
    }
  }


  const [anchorElD, setAnchorElD] = useState(null);
  const handleMenuClickD = (event) => setAnchorElD(event.currentTarget);
  const handleMenuCloseD = () => setAnchorElD(null);

  return (
    <>
      <CenterLoading open={loading} />
      <Box
        ref={headerRef}
        className="site-header"
        sx={{
          flexGrow: 1,
          background: "#f4eee6",
          position: "sticky",
          top: 0,
          zIndex: 1100,
        }}
      >
        <Container maxWidth="lg">
          <Toolbar
            disableGutters
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              minHeight: { xs: "52px !important", sm: "64px !important" },
              py: 0.5,
            }}
          >
            <Box className="logo-container">
              <Link to="/">
                <img src={Logo} alt="Japa Health" />
              </Link>
            </Box>

            {token ? (
              <Box className="header__welcomeuser" sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                {!isSubscription && (
                  <Button
                    component={Link}
                    to="/plans"
                    variant="contained"
                    sx={{
                      backgroundColor: "#FEBD36",
                      color: "#1a1a14",
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      boxShadow: "none",
                      px: 1.5,
                      py: 0.6,
                      minWidth: 0,
                      "&:hover": { backgroundColor: "#E5A82B", boxShadow: "none" },
                    }}
                  >
                    Join
                  </Button>
                )}
                <Box
                  onClick={handleMenuClickD}
                  sx={{
                    cursor: "pointer",
                    fontWeight: 500,
                    display: "flex",
                    alignItems: "center",
                    whiteSpace: "nowrap",
                    lineHeight: 1.2,
                    gap: "2px",
                    color: "#1c2424",
                    fontSize: { xs: "0.9rem", sm: "1rem" },
                  }}
                >
                  Welcome {props.user.firstName || ""}
                  <ExpandMoreIcon sx={{ fontSize: 22, color: "#1c2424" }} />
                </Box>
                <Menu
                  anchorEl={anchorElD}
                  open={Boolean(anchorElD)}
                  onClose={handleMenuCloseD}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  PaperProps={{
                    sx: {
                      mt: 1,
                      minWidth: 220,
                      background: "#fffaf3",
                      border: "1px solid rgba(28,36,36,0.08)",
                      boxShadow: "0 8px 24px rgba(14,27,29,0.12)",
                    },
                  }}
                >
                  {canShowOrderLabs && (
                    <MenuItem onClick={handleOrderLabsClick}>
                      {canUseOrderLabs
                        ? "Order Labs"
                        : "Join to Get 43% Off Labs"}
                    </MenuItem>
                  )}
                  {canAccessBookConsult && (
                    <MenuItem
                      onClick={() => {
                        handleMenuCloseD();
                        handleBookConsultClick();
                      }}
                    >
                      Book a Consult
                    </MenuItem>
                  )}
                  {headerLinks
                    .filter((link) => isHeaderLinkVisible(link))
                    .map((link) => {
                      const currLocation = location.pathname === link.pathname;
                      const isLastSurvey =
                        surveyIdItem?.SurveyId === parseInt(localStorage.getItem("assesmentKey"));
                      const isActive =
                        (location.pathname !== "/result" && currLocation) ||
                        (link.id === "home" &&
                          isLastSurvey &&
                          ["/progress", "/result", "/profiledetail"].includes(location.pathname));
                      return (
                        <MenuItem
                          component={Link}
                          to={link.pathname}
                          onClick={(event) => {
                            handleMenuCloseD();
                            getRedirectPath(event, link);
                          }}
                          key={link.id}
                          className={isActive ? "menuItem__active-link" : ""}
                        >
                          {link.label}
                        </MenuItem>
                      );
                    })}
                  <Divider />
                  <MenuItem
                    onClick={() => {
                      handleLogout();
                      handleMenuCloseD();
                    }}
                  >
                    Logout
                  </MenuItem>
                </Menu>
              </Box>
            ) : null}
          </Toolbar>
        </Container>
      </Box>


      <Modal
        open={openZohoLabModal}
        onClose={closeZohoLabModal}
      >
        <Box
          sx={{
            ...modalStyle,
            width: { xs: "95%", sm: "90%", md: "80%" },
            maxWidth: "960px",
            height: { xs: "90vh", md: "85vh" },
            display: "flex",
            flexDirection: "column",
            p: 0,
            pb: 0,
          }}
        >
          <Box className="close-pop-wrap">
            <Box className="Heading">
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", color: "#fff" }}
              >
                Order Labs
              </Typography>
            </Box>
            <CloseIcon
              onClick={closeZohoLabModal}
              className="CloseImg"
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              minHeight: 450,
              width: "100%",
              overflow: "auto",
              background: "#fff",
              display: "flex",
              flexDirection: "column",
              "& iframe": {
                width: "100% !important",
                maxWidth: "100% !important",
                flex: 1,
                minHeight: 450,
                border: 0,
              },
            }}
          >
            {openZohoLabModal && zohoScript ? (
              <ZohoScriptEmbed html={zohoScript} title="Order Labs Zoho Form" />
            ) : null}
          </Box>
        </Box>
      </Modal>

      <OrderLabsDealModal
        open={openModal}
        variant={canUseOrderLabs ? "member" : "join"}
        onClose={() => setOpenModal(false)}
        onConfirm={
          canUseOrderLabs ? handleSendInviteForLabs : goToMemberships
        }
        pricing={labPricing}
      />


      <Modal open={openConfirmModal} onClose={() => setOpenConfirmModal(false)}>
        <Box sx={modalStyle} width={"fit-content"} maxWidth={"600px"} minWidth={"350px"}>
          <Box className="close-pop-wrap">
            <Box className="Heading">
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", color: "#fff", letterSpacing: "1px" }}
              >
                Email Sent
              </Typography>
            </Box>
            <CloseIcon onClick={() => setOpenConfirmModal(false)} className="CloseImg" />
          </Box>
          <Box className="ContentWrapper">
            <Typography>
              🎉 Awesome! Check the email you registered with for instructions. 📩
            </Typography>
          </Box>
        </Box>
      </Modal>


      <MembersOnlyModal
        open={openMembershipModal}
        onClose={() => setOpenMembershipModal(false)}
        message={membershipModalMessage}
      />

      <SnackBar
        open={openSnackBar?.open}
        message={openSnackBar?.message}
        severity={openSnackBar?.severity}
        handleCloseSnackBar={() => {
          setOpenSnackBar({
            open: false,
            message: '',
            severity: ''
          })
        }}
      />
    </>
  );
};

const actionCreators = { getSurveyList, getUserData };

export default connect(({ user }) => ({ user }), actionCreators)(Header);


const modalStyle = {
  overflow: "hidden",
  position: "relative",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90vh",
  backgroundColor: "white",
  boxShadow: 24,
  pb: 2,
  borderRadius: "8px",
  ".close-pop-wrap": {
    top: "0px",
    right: "0px",
    position: "sticky",
    padding: "10px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#112225",
    img: {
      width: "24px",
      height: "24px",
    },
  },
  ".Heading": {
    display: "flex",
    alignItems: "center",
    h2: {
      mb: 0,
      color: "#fff",
    },
    svg: {
      mr: 1,
      width: "20px",
      height: "20px",
    },
  },
  ".CloseImg": {
    opacity: 1,
    borderRadius: "50%",
    p: "2px",
    border: "1px solid #fff",
    fontSize: "0.7rem",
    path: {
      fill: "#fff",
    },
  },
  ".ContentWrapper": {
    p: 2,
  },
};